import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { inject, observer } from 'mobx-react'
import styled from 'styled-components'
import { computed } from 'mobx'
import { uniqueId } from 'lodash-es'

import Skeleton from 'components/Skeleton'
import CouponCard from 'components/coupons/CouponCard'
import CouponsHeader, { Tab } from 'components/coupons/CouponsNavigation'
import { mediaQueriesBreakpoints, respondAbove } from 'styles/mixins'
import { CouponFilter } from 'mobx/Coupons/types'
import CouponsFilters, { filterToString } from 'components/coupons/CouponsFilters'
import AddManualCoupon from 'components/coupons/AddManualCoupon'
import EmptyState from 'components/coupons/EmptyState'
import TypographyPro from 'themes/TypographyPro'
import type { OrderType, Coupon, NonExternalCoupon } from 'shared-types/Coupon'
import { codeToLocale, getTranslatedTextByKey } from 'utils/utils'
import { Page } from 'utils/constants'
import { useStores } from 'hooks/useStores'
import { useCurrentUrlInfo } from 'hooks/useCurrentUrlInfo'
import CouponCardSkeleton from 'components/coupons/CouponCardSkeleton'
import { sendCustomEvent } from 'utils/analytics/analytics'

import type _Infra from 'mobx/Infra'
import type _Account from 'mobx/Account'
import type _Session from 'mobx/Session'
import type { Filter } from 'mobx/Coupons/types'
import type { LanguageCode } from 'utils/language'
import { EXTERNAL_ORDER_TYPES } from 'types/Coupons'
import { isExternal } from 'mobx/Coupons/utils'

const CouponListContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: stretch;
	align-self: stretch;
	justify-content: flex-start;
	gap: 1em;

	background: var(-clear);
`

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.75rem;

	padding: 1.75rem 0.62rem 5%;

	@media ${mediaQueriesBreakpoints.xs} {
		padding: 1.75rem 1.25rem 5%;
	}

	@media ${mediaQueriesBreakpoints.md} {
		flex-direction: row;
		padding: 1.75rem 2.5rem 5%;
	}
`

const CouponsContainer = styled.div`
	width: 100%;
`

const CategoryListContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.75rem;
	padding-top: 1.75rem;
	padding-bottom: 25%;

	${respondAbove.md`
			padding-bottom: 0;
	`}
`

const CouponTypeTitle = styled(TypographyPro).attrs({ variant: 'h3' })`
	color: var(--strokeMenuTitle);
`

const CategoryDescription = styled(TypographyPro).attrs({ variant: 'Subhead' })`
	color: var(--inactive);
`

const filterOrder = [
	CouponFilter.ALL,
	CouponFilter.MY_COUPONS,
	CouponFilter.DELIVERY,
	CouponFilter.PICKUP,
	CouponFilter.KIOSK,
	CouponFilter.IN_STORE,
	CouponFilter.DRIVETHROUGH,
]

const orderByType = (a: Filter, b: Filter) => filterOrder.indexOf(a) - filterOrder.indexOf(b)

const SKELETON_COUNT = 9

const Coupons: React.FC<{ Infra?: typeof _Infra; Account?: typeof _Account; Session: typeof _Session }> = ({
	Infra,
	Account,
	Session,
}: {
	Infra?: typeof _Infra
	Account?: typeof _Account
	Session: typeof _Session
}) => {
	const { couponsStore, couponFlowStore, addressManagerV2Store } = useStores()
	const [internalLoading, setInternalLoading] = useState(true)
	const [externalLoading, setExternalLoading] = useState(true)
	const [tab, setTab] = useState<Tab>(Tab.SPECIAL_OFFERS)
	const [filter, setFilter] = useState<Filter>(CouponFilter.ALL)
	const [disableFilterSwitching, setDisableFilterSwitching] = useState(false)
	const [disableInteractions, setDisableInteractions] = useState(false)
	const disableCouponsOfOrderType = (Infra?.appParams?.features?.disableCouponsOfOrderType ?? []) as OrderType[]
	const hasLoadedInitially = useRef<boolean>(false)
	const hasLoadedInitiallyAuthenticated = useRef<boolean>(true)

	// The dependency should be specifically couponStore.coupons, and not the entire couponStore
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const isDisabled = useMemo(() => couponsStore.filterCoupons(CouponFilter.ALL).length === 0, [couponsStore.coupons])

	const { dynamicRouteQuery } = useCurrentUrlInfo()

	useEffect(() => {
		setDisableInteractions(couponFlowStore.couponToApply !== null)
	}, [couponFlowStore.couponToApply])

	const loadInternalCoupons = useCallback(async () => {
		setInternalLoading(true)
		await couponsStore.fetchInternalCoupons()
		if (!Account?.isAuthenticated()) {
			setInternalLoading(false)
			hasLoadedInitiallyAuthenticated.current = false
			return
		}

		setInternalLoading(false)
	}, [Account, couponsStore])

	const loadExternalCoupons = useCallback(async () => {
		await couponsStore.fetchExternalCoupons()
		setExternalLoading(false)
	}, [couponsStore])

	const redirectToMyCoupons = useCallback(() => {
		// Hack to make sure that disableFilterSwitching is up-to-date
		setDisableFilterSwitching((prevDisableFilterSwitching) => {
			if (!prevDisableFilterSwitching) {
				setFilter((prev) => (couponsStore.filterCoupons(CouponFilter.MY_COUPONS).length ? CouponFilter.MY_COUPONS : prev))
			}

			return prevDisableFilterSwitching
		})
		// The dependency should be specifically couponStore.coupons, and not the entire couponStore
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [couponsStore.coupons, disableFilterSwitching])

	const loadCoupons = useCallback(async () => {
		// no await here, since PRB is slow to load, and therefore should not block the redirection
		loadExternalCoupons()
		await loadInternalCoupons()

		redirectToMyCoupons()
		
	}, [loadInternalCoupons, loadExternalCoupons, redirectToMyCoupons])

	useEffect(() => {
		const initPage = async () => {
			const orderType = addressManagerV2Store.formattedOrderTypeForSession()
			await Session.initOrUpdateSession({ refObject: { orderType }, shouldRedirectIfError: false })

			loadCoupons()
		}

		if (!hasLoadedInitially.current) {
			hasLoadedInitially.current = true
			initPage()
		}
		// We need to make sure this is called only once due to the initOrUpdateSession function
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (!hasLoadedInitiallyAuthenticated.current && Account?.isAuthenticated()) {
			hasLoadedInitiallyAuthenticated.current = true
			loadCoupons()
		}
	}, [Account, Account?.user, loadCoupons])

	const locale = codeToLocale[(dynamicRouteQuery.lang as LanguageCode) ?? 'en']

	// The dependency should be specifically couponStore.coupons, and not the entire couponStore

	const groupedCoupons: Record<Filter, Coupon[]> = useMemo(
		() => computed(() => couponsStore.getGroupedCoupons(filter)),
		[couponsStore, filter]
	).get()

	const isEmpty = Object.keys(groupedCoupons).length === 0

	const handleApply = (coupon: NonExternalCoupon) => {
		setDisableInteractions(true)

		if (isExternal(coupon)) {
			return
		}

		sendCustomEvent({
			category: 'apply_coupon',
			action: 'clicked',
			coupon_name: coupon.title[Infra?.locale?.msg as string],
			coupon_code: coupon.code,
			price: coupon.price,
			order_type: [...coupon.orderTypes],
			offer_type: coupon.offerType,
			page_path: window.location.pathname,
		})
	}

	const handleViewInfo = (coupon: Coupon) => {
		couponsStore.setCouponModal(coupon)

		const external = isExternal(coupon)

		sendCustomEvent({
			category: 'view_coupon_details',
			action: 'clicked',
			coupon_code: external ? 'external' : coupon.code,
			coupon_name: coupon.title[Infra?.locale?.msg as string],
			price: coupon.price,
			order_type: [...coupon.orderTypes],
			offer_type: external ? 'external' : coupon.offerType,
			page_path: window.location.pathname,
		})
	}

	const content = (
		<CategoryListContainer>
			{(Object.entries(groupedCoupons) as [Filter, Coupon[]][])
				.sort(([a], [b]) => orderByType(a, b))
				.map(([type, coupons]) => (
					<React.Fragment key={type}>
						<CouponTypeTitle data-testid={`coupon-title-for-${type}`}>
							{type
								.split(',')
								.map((part) => getTranslatedTextByKey(`eCommerce.coupons.filter.${part}`, filterToString[part as Filter]))
								.join(' + ')
								.toUpperCase()}
						</CouponTypeTitle>
						{type === CouponFilter.KIOSK && (
							<CategoryDescription>
								{getTranslatedTextByKey(
									'eCommerce.coupons.filter.kioskDescription',
									'Discover coupons you can use when ordering at the in-store kiosk - just scan the QR code to apply.'
								)}
							</CategoryDescription>
						)}
						<CouponListContainer data-testid="coupon-list-container">
							{coupons.map((coupon: Coupon) => (
								<CouponCard
									data-testid="coupon-card"
									key={`${coupon.id}-${isExternal(coupon) ? 'external' : coupon.code}`}
									coupon={coupon}
									locale={locale}
									setDisableInteractions={setDisableInteractions}
									onViewInfo={() => handleViewInfo(coupon)}
									onApply={handleApply}
									disabled={disableInteractions}
								/>
							))}
						</CouponListContainer>
					</React.Fragment>
				))}
		</CategoryListContainer>
	)

	const loadingContent = useMemo(
		() => (
			<CategoryListContainer>
				<CouponListContainer>
					{Array.from({ length: SKELETON_COUNT }, () => (
						<CouponCardSkeleton key={uniqueId()} />
					))}
				</CouponListContainer>
			</CategoryListContainer>
		),
		[]
	)

	const emptyOrContent = isEmpty ? <EmptyState filter={filter} onSignIn={() => setDisableFilterSwitching(true)} /> : content
	const loadingOrExternalContent = externalLoading ? loadingContent : emptyOrContent
	const loadingOrInternalContent = internalLoading ? loadingContent : emptyOrContent

	return (
		<Skeleton
			page={Page.COUPONS}
			showCart={addressManagerV2Store.alreadyLocalized || addressManagerV2Store?.isUserLocalized()}
			showLogo
			showHeader
		>
			<Container>
				<CouponsHeader tab={tab} setTab={setTab} />
				<CouponsContainer>
					<AddManualCoupon locale={Infra?.locale?.msg as string} />
					<CouponsFilters
						disabled={isDisabled}
						filter={filter}
						setFilter={(f) => {
							setDisableFilterSwitching(true)
							setFilter(f)
						}}
						filterOrder={filterOrder}
						ignoredFilters={disableCouponsOfOrderType}
					/>
					{EXTERNAL_ORDER_TYPES.includes(filter as OrderType) ? loadingOrExternalContent : loadingOrInternalContent}
				</CouponsContainer>
			</Container>
		</Skeleton>
	)
}

export default inject('Infra', 'Session', 'Account')(observer(Coupons))
